import { type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { siteConfig } from '@/config/site'
import footerBg from '@/assets/images/footer.png'
import logoUrl from '@/assets/images/Codefied..svg'
import instagram from '@/assets/images/Instagram.svg'
import facebook from '@/assets/images/Facebook.svg'
import linkedin from '@/assets/images/Twitter.svg'

const socialLinks = {
  instagram: 'https://www.instagram.com/codefied/',
  linkedin: 'https://www.linkedin.com/company/codefied-co/posts/?feedView=all',
  facebook: 'https://www.facebook.com/Codefied.co',
}

type FooterLink = { label: string; href: string }

const columns: { group: string; links: FooterLink[] }[] = [
  {
    group: 'Home',
    links: [
      { label: 'Services', href: '/#services' },
      { label: 'Products', href: '/#products' },
      { label: 'Process', href: '/#process' },
      { label: 'Why Codefied', href: '/#why-codefied' },
    ],
  },
  {
    group: 'Back Office',
    links: [
      { label: 'For Teams', href: '/back-office#for-teams' },
      { label: 'Where We Step In', href: '/back-office#step-in' },
      { label: 'Capabilities', href: '/back-office#capabilities' },
      { label: 'Execution', href: '/back-office#execution' },
      { label: 'What We Offer', href: '/back-office#offer' },
      { label: 'Success Stories', href: '/back-office#success-stories' },
    ],
  },
  {
    group: 'Product Engineering',
    links: [
      { label: 'For Teams', href: '/product-engineering#for-teams' },
      { label: 'Our Approach', href: '/product-engineering#approach' },
      { label: 'Capabilities', href: '/product-engineering#capabilities' },
      { label: 'Execution', href: '/product-engineering#execution' },
      { label: 'What We Offer', href: '/product-engineering#offer' },
      { label: 'Success Stories', href: '/product-engineering#success-stories' },
    ],
  },
  {
    group: 'About',
    links: [
      { label: 'Philosophy', href: '/about#philosophy' },
      { label: 'Our Story', href: '/about#story' },
      { label: 'Timeline', href: '/about#timeline' },
      { label: 'Team', href: '/about#team' },
      { label: 'Global Presence', href: '/about#presence' },
    ],
  },
  {
    group: 'Careers',
    links: [{ label: 'Open Positions', href: '/careers#open-positions' }],
  },
  {
    group: 'Blog',
    links: [{ label: 'All Posts', href: '/blog' }],
  },
]

const legalLinks: FooterLink[] = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms & Conditions', href: '/terms-and-conditions' },
]

export function Footer() {
  const year = new Date().getFullYear()

  return (
    <footer
      className="relative overflow-hidden"
      style={{
        backgroundImage: `url(${footerBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="container-site relative z-10 pt-10 sm:pt-14 xl:pt-16">
        {/* Logo — full width row on top */}
        <div className="mb-8 xl:mb-10">
          <a href="/" aria-label="Codefied home">
            <img src={logoUrl} alt="Codefied Logo" width="126" height="24" className="h-auto w-auto" />
          </a>
        </div>

        {/* Bottom row: nav columns + social card */}
        <div className="flex flex-col gap-10 xl:flex-row xl:items-start xl:gap-[72px]">
          {/* Nav columns — 2 cols on mobile, 3 on sm, 6 on xl */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 xl:flex xl:flex-1 xl:gap-[72px]">
            {columns.map(({ group, links }) => (
              <div key={group}>
                <h3 className="mb-3 font-outfit text-[16px] font-medium not-italic leading-normal text-[#292929]">
                  {group}
                </h3>
                <ul className="flex flex-col gap-2">
                  {links.map(({ label, href }) => (
                    <li key={label}>
                      <Link
                        to={href}
                        className="font-outfit text-[12px] font-normal not-italic leading-normal text-[#292929] transition-opacity hover:opacity-70"
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Social card */}
          <div className="flex w-[280px] shrink-0 flex-col items-start gap-[18px] rounded-[10px] bg-white/80 p-5">
            {/* Header */}
            <div className="w-full flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Avatar */}
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#B0383C] font-sans text-[18px] font-bold text-white">
                  C.
                </div>
                <div>
                  <p className="font-poppins text-[14px] font-medium not-italic leading-normal text-black">
                    Codefied
                  </p>
                  <p className="mt-0.5 font-poppins text-[12px] font-normal not-italic leading-normal text-black">
                    @codefiedofficial
                  </p>
                </div>
              </div>
              {/* LinkedIn icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M15.053 0H4.447C3.26758 0 2.13647 0.468522 1.3025 1.3025C0.468522 2.13647 0 3.26758 0 4.447V15.053C0 16.2324 0.468522 17.3635 1.3025 18.1975C2.13647 19.0315 3.26758 19.5 4.447 19.5H15.053C16.2324 19.5 17.3635 19.0315 18.1975 18.1975C19.0315 17.3635 19.5 16.2324 19.5 15.053V4.447C19.5 3.26758 19.0315 2.13647 18.1975 1.3025C17.3635 0.468522 16.2324 0 15.053 0ZM6.593 15.742C6.59614 15.7964 6.58811 15.8509 6.56942 15.9021C6.55072 15.9534 6.52176 16.0002 6.4843 16.0398C6.44684 16.0794 6.40167 16.111 6.35158 16.1325C6.30149 16.154 6.24752 16.1651 6.193 16.165H4.413C4.30566 16.1624 4.20362 16.1178 4.12883 16.0407C4.05403 15.9637 4.01244 15.8604 4.013 15.753V8.35C4.01153 8.29655 4.02079 8.24335 4.04023 8.19355C4.05967 8.14374 4.0889 8.09834 4.1262 8.06002C4.16349 8.0217 4.20808 7.99125 4.25734 7.97047C4.3066 7.94968 4.35953 7.93898 4.413 7.939H6.193C6.24647 7.93898 6.2994 7.94968 6.34866 7.97047C6.39792 7.99125 6.44251 8.0217 6.4798 8.06002C6.5171 8.09834 6.54633 8.14374 6.56577 8.19355C6.58521 8.24335 6.59447 8.29655 6.593 8.35V15.742ZM5.27 6.382C5.07729 6.38056 4.88674 6.34117 4.70925 6.26608C4.53176 6.191 4.37079 6.08169 4.23554 5.9444C4.10029 5.80711 3.99341 5.64452 3.921 5.46593C3.84858 5.28733 3.81206 5.09621 3.8135 4.9035C3.81494 4.71079 3.85433 4.52024 3.92942 4.34275C4.0045 4.16526 4.11381 4.00429 4.2511 3.86904C4.38839 3.73379 4.55098 3.62691 4.72957 3.5545C4.90817 3.48208 5.09929 3.44556 5.292 3.447C5.67452 3.45944 6.03703 3.62087 6.30219 3.89686C6.56734 4.17284 6.71415 4.54152 6.71128 4.92423C6.7084 5.30694 6.55609 5.67337 6.28682 5.94535C6.01755 6.21732 5.65266 6.3733 5.27 6.38M16.087 15.73C16.0865 15.831 16.0469 15.928 15.9764 16.0003C15.9058 16.0727 15.81 16.1149 15.709 16.118H13.83C13.7288 16.1149 13.6328 16.0726 13.5623 16C13.4918 15.9274 13.4522 15.8302 13.452 15.729V12.305C13.452 11.794 13.608 10.082 12.096 10.082C10.917 10.082 10.684 11.282 10.639 11.816V15.807C10.639 15.9084 10.5996 16.0058 10.529 16.0786C10.4585 16.1514 10.3623 16.1939 10.261 16.197H8.438C8.33474 16.1967 8.2358 16.1555 8.16287 16.0824C8.08995 16.0093 8.049 15.9103 8.049 15.807V8.314C8.05212 8.21266 8.09458 8.11652 8.16739 8.04595C8.24019 7.97539 8.33761 7.93595 8.439 7.936H10.261C10.3624 7.93595 10.4598 7.97539 10.5326 8.04595C10.6054 8.11652 10.6479 8.21266 10.651 8.314V8.959C10.917 8.56962 11.2843 8.26027 11.7133 8.0643C12.1422 7.86834 12.6165 7.7932 13.085 7.847C16.12 7.847 16.109 10.682 16.109 12.294L16.087 15.73Z"
                  fill="black"
                />
              </svg>
            </div>
            {/* Post text */}
            <p className="mt-3 font-poppins text-[14px] font-normal not-italic leading-[20px] text-black">
              We've just announced a new project that we have undertaken centered around remote
              automated operation of a freight company...Stay tuned for more.
            </p>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="relative z-10 mt-8 flex flex-col items-start justify-between gap-3 pb-6 pt-5 sm:flex-row sm:items-center xl:mt-12 xl:pb-8 xl:pt-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
            <p className="font-outfit text-[12px] font-light not-italic leading-[140%] tracking-[-0.72px] text-[#292929]">
              {siteConfig.name}. All Rights Reserved &copy; {year}
            </p>
            <ul className="flex items-center gap-4">
              {legalLinks.map(({ label, href }) => (
                <li key={label}>
                  <Link
                    to={href}
                    className="font-outfit text-[12px] font-normal not-italic leading-[140%] text-[#292929] underline-offset-2 transition-opacity hover:underline hover:opacity-70"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social icon circles */}
          <div className="flex items-center gap-3">
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="transition-opacity hover:opacity-70"
            >
              <img src={instagram} alt="Instagram" />
            </a>
            <a
              href={socialLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="transition-opacity hover:opacity-70"
            >
              <img src={facebook} alt="Facebook" />
            </a>
            <a
              href={socialLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="transition-opacity hover:opacity-70"
            >
              <img src={linkedin} alt="LinkedIn" />
            </a>
          </div>
        </div>
      </div>

      {/* Large "Codefied" watermark — bottom, clipped, opacity 0.2 */}
      <div
        className="pointer-events-none absolute bottom-0 left-0 right-0 select-none overflow-hidden"
        aria-hidden
      >
        <p
          className="text-center font-semibold text-white"
          style={
            {
              fontFamily: 'Poppins, sans-serif',
              fontSize: 'clamp(80px, 20vw, 300px)',
              fontWeight: 600,
              lineHeight: '140%',
              opacity: 0.2,
              leadingTrim: 'both',
              textEdge: 'cap',
              marginBottom: '-0.25em',
            } as CSSProperties
          }
        >
          Codefied
        </p>
      </div>
    </footer>
  )
}
